type CacheFetchOptions<T> = {
  cacheKey: string;
  storage: Record<string, any>;
  setStorage: (key: string, value: any) => void;
  fetcher: () => Promise<T>;
  isValid?: (data: any) => boolean;
};

/**
 * Fetches data from cache or API.
 *
 * @param {CacheFetchOptions<T>} options
 * @param {string} options.cacheKey - Key to store the data in the cache
 * @param {Record<string, any>} options.storage - The cache storage
 * @param {(data: Record<string, any>) => void} options.setStorage - Function to set the cache storage
 * @param {() => Promise<T>} options.fetcher - Function to fetch the data from the API
 * @param {(data: any) => boolean} options.isValid - Function to check if the data is valid. Defaults to checking if the data is an array.
 * @returns {Promise<T>} - The fetched data
 */
export async function getFromCacheOrFetch<T>({
  cacheKey,
  storage,
  setStorage,
  fetcher,
  isValid = (data) => Array.isArray(data),
}: CacheFetchOptions<T>): Promise<T> {
  const cached = storage?.[cacheKey];

  // Use cache if valid
  if (cached && isValid(cached)) {
    return cached as T;
  }

  // Fetch from API
  const freshData = await fetcher();

  if (!freshData) return freshData;

  // Save to cache
  setStorage(cacheKey, freshData);

  return freshData;
}
